import React, { useMemo } from 'react';
import { useQuery, useParams } from '@common/hooks';
import { SUPPLIERS_QUERY, SupplierListItemType } from '@institutions/graphql';
import {
  AddButton,
  ErrorHandler,
  Flex,
  TableTransfer,
  Link,
} from '@common/components';
import { filterableColumnBuilder } from 'works/legacy/lib/table-filters-helper';

const { filterOptionForColumns } = TableTransfer;

function generateRowKey(data: SupplierListItemType[]) {
  return data.map((supplier) => ({
    key: supplier.id,
    ...supplier,
  }));
}

const column = filterableColumnBuilder<SupplierListItemType>({});

const tableColumns = [
  column({
    key: 'name',
    dataIndex: 'name',
    title: 'Supplier Name',
  }),
];

type Props = {
  selectedSuppliers: SupplierListItemType[];
  onSelectedSuppliersChange: (suppliers: SupplierListItemType[]) => void;
};

export function SuppliersManagementInvitationStepSelectSupplier({
  selectedSuppliers,
  onSelectedSuppliersChange,
}: Props) {
  const params = useParams<{ supplierId: string }>();

  const selectedSuppliersIds = useMemo(
    () => selectedSuppliers.map((supplier) => supplier.id),
    [selectedSuppliers]
  );

  const { loading, error, data } = useQuery(SUPPLIERS_QUERY, {
    variables: {
      input: {
        filters: {
          status: ['AVAILABLE', 'INVITED', 'DECLINED', 'PENDING_ACTIVATION'],
        },
        orders: {
          status: 'DESC',
        },
      },
    },
    onCompleted: () => onChange([params?.supplierId]),
  });

  if (error) {
    return <ErrorHandler error={error} log="supplier-invitation-step-one" />;
  }

  const allSuppliers = data?.suppliers || [];

  function getSelectedSuppliers(supplierIds: string[]) {
    return allSuppliers.filter(({ id }) => supplierIds.includes(id));
  }

  function onChange(supplierIds: string[]) {
    const newSelectedSuppliers = getSelectedSuppliers(supplierIds);

    onSelectedSuppliersChange(newSelectedSuppliers);
  }

  const filterOption = filterOptionForColumns(
    tableColumns.map(({ dataIndex }) => dataIndex)
  );

  const newSupplierTitleWithAction = (title: string) => (
    <Flex justify="space-between" align="center">
      <span>{title}</span>{' '}
      <Link to="/works/admin/suppliers/new">
        <AddButton>New Supplier</AddButton>
      </Link>
    </Flex>
  );

  const transferTitles = {
    left: newSupplierTitleWithAction('Available'),
    right: 'Selected',
  };

  return (
    <TableTransfer
      showSearch
      headerLarge
      titles={transferTitles}
      className="suppliers-invitation-list"
      disabled={loading}
      loading={loading}
      columns={tableColumns}
      dataSource={generateRowKey(allSuppliers)}
      targetKeys={selectedSuppliersIds}
      onChange={onChange}
      filterOption={filterOption}
    />
  );
}
